import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';

import { isEmptyArray, isNullish } from '../../common/utils/nullish';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './sector-attribution-chart.constants.js';
import type { SectorAttributionChartContentObject } from './sector-attribution-chart.model.js';
import { restrictLastLegendItemClick } from '../../common/utils/sharedChartOptions.js';

/**
 * @tag pop-sector-attribution-chart-component
 * @attribute { SectorAttributionChartContentObject } [data-component-object] SectorAttributionChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorAttributionChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorAttributionChart content object
   */
  componentObject?: SectorAttributionChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');

    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getCategories() {
    return (
      this.componentObject?.items?.map((item) =>
        item.name ? item.name : '',
      ) || []
    );
  }

  private getSeriesData() {
    const totalValueAddedSeries =
      this.componentObject?.items?.map((item) => ({
        y: item.totalValueAddedPercent,
        name: item.name,
      })) || [];

    const criteriaWeightSeries =
      this.componentObject?.items?.map((item) => ({
        y: item.criteriaWeightValuePercent,
        name: item.name,
      })) || [];

    const stockSelectionSeries =
      this.componentObject?.items?.map((item) => ({
        y: item.stockSelectionValuePercent,
        name: item.name,
      })) || [];

    return [
      {
        name: `${Constants.TOTAL_VALUE_ADDED_LABEL} %`,
        data: totalValueAddedSeries,
      },
      {
        name: `${Constants.VALUE_FROM_SECTOR_WEIGHT_LABEL} %`,
        data: criteriaWeightSeries,
      },
      {
        name: `${Constants.VALUE_FROM_STOCK_SELECTION_LABEL} %`,
        data: stockSelectionSeries,
      },
    ];
  }

  private getOptions(): Options {
    return {
      tooltip: {
        shared: true,
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter() {
          return BeaconChartBase.tooltipPointHTML(
            this,
            this.series.name,
            `${formatToNDecimalPlaces(this.y, 2)}%`,
          );
        },
      },
      exporting: { enabled: false },
      legend: restrictLastLegendItemClick(),
      xAxis: { categories: this.getCategories() },
    };
  }

  render() {
    if (
      isEmptyArray(this.componentObject?.items) ||
      isNullish(this.componentObject?.items)
    ) {
      return html``;
    }
    return this.renderComponent(
      html`<beacon-bar-chart
        variant="vertical"
        tooltip-variant="multi-level"
        series="${JSON.stringify(this.getSeriesData())}"
        options="${JSON.stringify(this.getOptions())}"
        y-axis-label="${Constants.Y_AXIS_LABEL}"
        x-axis-label="${Constants.X_AXIS_LABEL}"
      >
      </beacon-bar-chart>`,
    );
  }
}
